import { useCallback, useEffect, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import Spinner from "../Components/Spinner";
import ErrorMessage from "../Components/ErrorMessage";
import MandatorySign from "../Components/MandatorySign";
import { MdCheck, MdOutlineCheckCircle, MdOutlineCircle, MdOutlineCopyAll } from "react-icons/md";
import { CiCircleAlert } from "react-icons/ci";
import { memoryOptions, cpuOptions, regionOptions } from "../MCPs/ContstantValues"
import { PiLeaf } from "react-icons/pi";
import SingleLoaderItem from "../Loaders/SingleLoaderItem";
import { BiCheck } from "react-icons/bi";
import { useLanguage } from "../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const GetAbilityModal = ({
    account_id,
    show,
    handleClose,
    selectedAbility
}) => {
    const {t} = useLanguage()
    const [loadingSave, setLoadingSave] = useState(false);
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });
    const [orgData, setOrgData] = useState([])
    const [loadingOrgData, setLoadingOrgData] = useState(false)
    const [openComputeSettings, setOpenComputeSettings] = useState(false)
    const [step, setStep] = useState(1)
    const [formData, setFormData] = useState({
        organization_ids: [],
        memory: memoryOptions[0],
        cpu: 1,
        region: regionOptions.find(r => r.value === "europe-west8"),
        max_instances: 1,
        network_mode: "restricted",
        tag: ""
    });

    const handleSave = async () => {
        setErrorMessage({ message: "", display: false });

        if (formData.organization_ids.length == 0) {
            setErrorMessage({ message: t.storage.org_required, display: true });
            return;
        }

        if (formData.max_instances > 4) {
            setErrorMessage({ message: t.abilities.max_four_inst, display: true });
            return;
        }

        if (selectedAbility.allow_multiple) {
            if (!formData.tag) {
                setErrorMessage({ message: t.directory.tag_required, display: true });
                return;
            }
        }

        setLoadingSave(true);

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/import_public_ability`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id: account_id,
                    abty_id: selectedAbility.id,
                    organization_ids: formData.organization_ids,
                    memory: formData.memory.value,
                    cpu: formData.cpu,
                    region: formData.region.value,
                    max_instances: formData.max_instances,
                    network_mode: formData.network_mode,
                    tag: formData.tag
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            setStep(2)

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const fetchOrgData = async (abilityId) => {
        setLoadingOrgData(true);

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_organizations_public`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id,
                    abty_id: abilityId
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch orgs");
            }

            setOrgData(result.data || []);
        } catch (err) {
            console.error("Error fetching organizations:", err);
        } finally {
            setLoadingOrgData(false);
        }
    };

    const handleOrgSelect = (orgId) => {
        setFormData(prev => {
            const alreadySelected = prev.organization_ids.includes(orgId);

            return {
                ...prev,
                organization_ids: alreadySelected
                    ? prev.organization_ids.filter(id => id !== orgId)
                    : [...prev.organization_ids, orgId]
            };
        });
    };

    const closeModal = () => {
        handleClose();
        setErrorMessage({ message: "", display: false });
        setFormData({
            organization_ids: [],
            memory: memoryOptions[0],
            cpu: 1,
            region: regionOptions.find(r => r.value === "europe-west8"),
            max_instances: 1,
            network_mode: "restricted",
            tag: ""
        })
        setStep(1)
        setLoadingOrgData(false)
        setLoadingSave(false)
        setOpenComputeSettings(false)
    };

    const toggleSettings = () => {
        openComputeSettings ? setOpenComputeSettings(false) : setOpenComputeSettings(true)
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (show && selectedAbility?.id) {
            fetchOrgData(selectedAbility.id);
        }
    }, [show, selectedAbility]);

    return (
        <Modal show={show} centered onHide={closeModal}>
            <div className="modal_header">
                <p className="modal_header_title">
                    {selectedAbility?.name}
                </p>
                <img className="ability_icon fade-in" src={selectedAbility?.icon} />
            </div>
            <p className="modal_header_title_description fade-in">{selectedAbility?.description}</p>
            {selectedAbility.status == "available" && step == 1 && (
                <p className="modal_header_title_description fade-in">{selectedAbility.name} {t.directory.verified}</p>
            )}
            {selectedAbility.status == "verifying" && step == 1 && (
                <p className="modal_header_title_description fade-in">{t.directory.not_verified}</p>
            )}
            {!selectedAbility.allow_multiple && step == 1 ? (
                <p className="modal_header_title_description fade-in">{selectedAbility.name} {t.directory.once}</p>
            ) : (
                <p className="modal_header_title_description fade-in">{selectedAbility.name} {t.directory.multiple}</p>
            )}

            {loadingOrgData ? (
                <div className="modal_main_cont">
                    <SingleLoaderItem length={1} />
                </div>
            ) : (
                step == 1 ? (
                    <div className="modal_main_cont">

                        <div className="label_input">
                            <label className="input_label">{t.sidebar.organization} <MandatorySign message={t.mandatory_field} /></label>
                            <Dropdown>
                                <Dropdown.Toggle
                                    as="div"
                                    className="register_form_first fade-in"
                                >
                                    {formData.organization_ids.length > 0 ? (
                                        `${formData.organization_ids.length} ${t.selected}`
                                    ) : (
                                        t.select
                                    )}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="custom_dropdown_menu">
                                    <div className="dropdown_container fade-in">
                                        {orgData.length > 0 ? (
                                            orgData.map((singleOrgData) => (
                                                (singleOrgData.role == "owner" || singleOrgData.role == "developer") ? (
                                                    <button
                                                        key={singleOrgData.id}
                                                        onClick={() => handleOrgSelect(singleOrgData.id)}
                                                        className={`settings_link ${formData.organization_ids.includes(singleOrgData.id) && "settings_link_selected"}`}
                                                    >
                                                        <i>
                                                            {formData.organization_ids.includes(singleOrgData.id)
                                                                ? <MdOutlineCheckCircle />
                                                                : <MdOutlineCircle />}
                                                        </i>
                                                        {singleOrgData.name}
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="settings_link settings_link_disabled"
                                                    >
                                                        {singleOrgData.name} <span className="settings_link_permissions">{t.directory.no_permission}</span>
                                                    </button>
                                                )
                                            ))
                                        ) : (
                                            <button
                                                className="settings_link"
                                            >
                                                {t.no_data_found}
                                            </button>
                                        )}

                                    </div>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {formData.organization_ids.length > 0 && selectedAbility.allow_multiple && (
                            <div className="label_input">
                                <label className="input_label">Tag <MandatorySign message={t.mandatory_field} /></label>
                                <input
                                    type="text"
                                    name="tag"
                                    placeholder="Ex. Ability-A"
                                    value={formData.tag}
                                    onChange={handleChange}
                                    className="register_form_first fade-in"
                                />
                            </div>
                        )}

                        {formData.organization_ids.length > 0 && (
                            <div className={`toggle_input_cont ${openComputeSettings ? "toggle_input_cont_open" : ""}`}>
                                <div className="toggle_input_cont_header" onClick={toggleSettings}>
                                    <p>{t.abilities.compute_settings}</p>
                                    <i className={`fa-solid fa-chevron-${openComputeSettings ? "up" : "down"}`}></i>
                                </div>
                                <div className={`toggle_input_cont_inner ${openComputeSettings ? "toggle_input_cont_inner_open" : ""}`}>
                                    <div className="persona_reg">
                                        <div className="label_input">
                                            <label className="input_label">{t.abilities.memory} <MandatorySign message={t.mandatory_field} /></label>
                                            <Dropdown drop="up">
                                                <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                                    {formData.memory.label}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className="custom_dropdown_menu">
                                                    {memoryOptions.map((mem) => (
                                                        <Dropdown.Item key={mem.value} onClick={() => setFormData({ ...formData, memory: mem })} className={`settings_link ${mem.value == formData.memory.value ? "settings_link_selected" : ""}`}>
                                                            {mem.label}
                                                        </Dropdown.Item>
                                                    ))}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>

                                        <div className="label_input">
                                            <label className="input_label">vCPU <MandatorySign message={t.mandatory_field} /></label>
                                            <Dropdown drop="up">
                                                <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                                    {formData.cpu} vCPU
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className="custom_dropdown_menu">
                                                    {cpuOptions.map((cpu) => {
                                                        const isDisable = cpu > formData.memory.maxCpu;
                                                        return (
                                                            <Dropdown.Item
                                                                key={cpu}
                                                                disabled={isDisable}
                                                                onClick={() => !isDisable && setFormData({ ...formData, cpu })}
                                                                className={`settings_link ${isDisable ? 'text-muted disabled_cpu' : ''} ${cpu == formData.cpu ? "settings_link_selected" : ""}`}
                                                            >
                                                                {cpu} vCPU {isDisable && "(Requires more RAM)"}
                                                            </Dropdown.Item>
                                                        );
                                                    })}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    </div>
                                    <div className="persona_reg">
                                        <div className="label_input">
                                            <label className="input_label">{t.abilities.scaling} <MandatorySign message={t.mandatory_field} /></label>
                                            <input
                                                type="number"
                                                name="max_instances"
                                                value={formData.max_instances}
                                                onChange={handleChange}
                                                min={1}
                                                max={5}
                                                className="register_form_first fade-in"
                                            />
                                        </div>
                                        <div className="label_input">
                                            <label className="input_label">{t.abilities.region} <MandatorySign message={t.abilities.region_warning} icon="info" /></label>
                                            <Dropdown drop="up">
                                                <Dropdown.Toggle as="div" className="register_form_first fade-in">
                                                    {formData.region.label}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className="custom_dropdown_menu">
                                                    <div className="scrollable_dropdown_cont">
                                                        {regionOptions.map((reg) => (
                                                            <Dropdown.Item
                                                                key={reg.value}
                                                                onClick={() => setFormData({ ...formData, region: reg })}
                                                                className={`settings_link ${reg.value == formData.region.value ? "settings_link_selected" : ""}`}
                                                            >
                                                                {reg.label}
                                                                <span>{reg.lowCo2 && <i><PiLeaf /></i>} {reg.value} </span>
                                                            </Dropdown.Item>
                                                        ))}
                                                    </div>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        )}

                        {formData.organization_ids.length > 0 && (
                            <div className="role_info_text_cont fade-in">
                                <p className="role_warning_text">
                                    <strong>{selectedAbility.name}</strong> {t.directory.warning_plublishing}
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="modal_main_cont">
                        <div className="confirmation_ability_get fade-in">
                            <p className="confirmation_ability_get_message"><BiCheck /> {t.directory.addedd_success}</p>
                            <p className="confirmation_ability_get_description">
                                {t.directory.confirmation}
                            </p>
                            <div className="confirmation_ability_get_btn_cont">
                                <a
                                    href="/e8console/organizations"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="confirmation_ability_get_btn fade-in"
                                >
                                    <span><i className="fa-solid fa-chevron-left"></i> {t.directory.go_to}</span>
                                </a>
                                <button className="confirmation_ability_get_btn fade-in" onClick={closeModal}>{t.directory.continue_dir}</button>
                            </div>
                        </div>
                    </div>
                )
            )}

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>

                {step == 1 && (
                    <div className="modal_footer_btn_cont">
                        {orgData.length > 0 && (
                            <button
                                className="modale_save_btn"
                                onClick={handleSave}
                                disabled={loadingSave}
                            >
                                {loadingSave ? <Spinner loading={true} /> : t.directory.get_ability}
                            </button>
                        )}

                        <button
                            className="modale_close_btn"
                            onClick={closeModal}
                            disabled={loadingSave}
                        >
                            {t.close}
                        </button>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default GetAbilityModal;